import { useState, useEffect, useRef, useCallback } from 'react';
import { get, post } from '../api';

// Public candidate-facing offer-letter signing page (mockup: offer-letter-signing_4).
// Rendered without login for the path /sign/:token. The candidate reads their
// filled offer letter, acknowledges I-9/E-Verify, signs (draw or type), submits.
const C = {
  bg: '#0a0702', card: '#1a1208', card2: '#2a1f0f', line: '#3a2a12', text: '#f5e6c8',
  muted: '#8a7055', gold: '#c9a96e', green: '#10b981', navy: '#1F4E79', blue: '#2E75B6',
};
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function today() { const d = new Date(); return MO[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear(); }

export default function OfferSign({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('draw');
  const [typed, setTyped] = useState('');
  const [drawn, setDrawn] = useState('');       // data URL from canvas
  const [i9, setI9] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    get('/offers/sign/' + token).then(setData).catch(e => setErr(e.message || 'This signing link is invalid.'));
  }, [token]);

  // ── Canvas drawing ──────────────────────────────────────────────────────────
  const setupCanvas = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ratio = window.devicePixelRatio || 1;
    cv.width = cv.offsetWidth * ratio; cv.height = cv.offsetHeight * ratio;
    const ctx = cv.getContext('2d'); ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#10243f';
  }, []);
  useEffect(() => { if (tab === 'draw' && data && !done) setTimeout(setupCanvas, 30); }, [tab, data, done, setupCanvas]);

  function pos(e) {
    const cv = canvasRef.current; const r = cv.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  }
  function start(e) { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); }
  function move(e) { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); dirty.current = true; }
  function end() { if (!drawing.current) return; drawing.current = false; if (dirty.current) setDrawn(canvasRef.current.toDataURL('image/png')); }
  function clearPad() { const cv = canvasRef.current; if (cv) cv.getContext('2d').clearRect(0, 0, cv.width, cv.height); dirty.current = false; setDrawn(''); }

  const signature = tab === 'draw' ? drawn : (typed.trim() ? typed.trim() : '');
  const signedName = data ? (typed.trim() || data.candidate_name) : '';
  const canSign = i9 && !!signature && !!signedName && !busy;

  async function submit() {
    setBusy(true); setErr('');
    try {
      const r = await post('/offers/sign/' + token, {
        signature, signature_type: tab, signed_name: signedName, i9_ack: i9,
      });
      setDone(r);
    } catch (e) { setErr(e.message || 'Could not submit your signature.'); }
    setBusy(false);
  }

  const page = { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans', system-ui, sans-serif", padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center' };

  if (err && !data) return <div style={page}><div style={{ maxWidth: 480, marginTop: 60, textAlign: 'center' }}><h2 style={{ color: C.gold }}>Tee &amp; Timber</h2><p style={{ color: C.muted }}>{err}</p></div></div>;
  if (!data) return <div style={page}><p style={{ color: C.muted, marginTop: 60 }}>Loading your offer…</p></div>;

  const f = data.fields || {};
  const F = x => <span style={{ background: '#fff7e6', padding: '0 2px', borderBottom: '1px dashed #c9a96e', fontWeight: 600 }}>{x || '—'}</span>;

  if (done || data.status === 'signed') {
    const name = done ? signedName : data.signed_name;
    return (
      <div style={page}>
        <div style={{ maxWidth: 480, marginTop: 50, textAlign: 'center', background: C.card, border: '1px solid ' + C.line, borderRadius: 16, padding: 28 }}>
          <div style={{ fontSize: 44 }}>✓</div>
          <h2 style={{ color: C.gold, margin: '6px 0' }}>Offer signed — welcome aboard!</h2>
          <p style={{ color: C.text }}>Thanks{name ? ', ' + String(name).split(' ')[0] : ''}! Your offer for <b>{data.fields?.title || data.role}</b> at Tee &amp; Timber is signed and on file.</p>
          <p style={{ color: C.muted, fontSize: 14 }}>We'll be in touch with next steps. On your first day, bring your I-9 documents (see below).</p>
          {(done && done.booking_link) && <a href={done.booking_link} style={{ display: 'inline-block', marginTop: 14, background: C.gold, color: '#1a1208', textDecoration: 'none', borderRadius: 999, fontWeight: 800, padding: '12px 22px' }}>Book your onboarding →</a>}
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={{ width: '100%', maxWidth: 780, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h1 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 800, color: C.gold }}>Offer Letter — Signing</h1>
        <span style={{ fontSize: '.58rem', fontWeight: 800, letterSpacing: '.06em', color: '#34d399', border: '1px solid #10b98155', background: '#10b9811a', borderRadius: 999, padding: '.2rem .5rem' }}>SECURE</span>
      </div>

      {/* The offer letter (paper) */}
      <div style={{ width: '100%', maxWidth: 780, background: '#fff', color: '#1a1a1a', borderRadius: 12, padding: 'clamp(20px,4.5vw,40px)', boxShadow: '0 10px 40px rgba(0,0,0,.5)', fontSize: 14, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 'clamp(20px,5vw,28px)' }}>TEE &amp; TIMBER</div>
        <div style={{ color: '#777', fontSize: 12 }}>{data.company.legal} · {data.company.address}</div>
        <div style={{ height: 2, background: C.blue, opacity: .4, margin: '14px 0 18px', borderRadius: 2 }} />
        <p>{today()}</p>
        <p>Dear {F(data.candidate_name)},</p>
        <p>We are pleased to offer you the position of {F(f.title || data.role)} at Tee &amp; Timber, a {F(f.type || 'Part-Time')} role reporting to {F(f.reports || data.company.signer)}.</p>
        <p>Your starting compensation is {F('$' + (f.comp || ''))} ({F(f.basis || 'Hourly')}), with a scheduled start date of {F(f.start)}.{f.returnby ? <> This offer is open until {F(f.returnby)}.</> : null}</p>
        <p style={{ fontStyle: 'italic', color: '#333', fontSize: 13 }}>Employment with Tee &amp; Timber is at-will, meaning either you or the company may end the employment relationship at any time, with or without cause or notice.</p>
        <div style={{ fontWeight: 800, color: C.navy, marginTop: 18 }}>I-9 / E-Verify</div>
        <p style={{ fontSize: 13.5 }}>Tee &amp; Timber participates in <b>E-Verify</b>. On or before your first day, federal law requires you to complete <b>Form I-9</b> and present original document(s) establishing your identity and work authorization — one document from <b>List A</b>, OR one each from <b>List B</b> and <b>List C</b> of the I-9 Lists of Acceptable Documents. RBS and Food Handler certifications are company-paid and due within 30 days of hire.</p>
        <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, marginTop: 20 }}>Tee &amp; Timber · Fremont, CA</div>
      </div>

      {/* Acknowledge + sign */}
      <div style={{ width: '100%', maxWidth: 780, background: C.card, border: '1px solid ' + C.line, borderRadius: 14, padding: 18, marginTop: 14 }}>
        <div style={{ fontWeight: 800, color: C.gold, marginBottom: 10 }}>Accept &amp; sign</div>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: C.text, fontSize: 14, cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={i9} onChange={e => setI9(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
          <span>I have read the offer above. I understand Tee &amp; Timber uses <b>E-Verify</b> and that I must complete <b>Form I-9</b> with acceptable documents on my first day. I accept this offer of employment.</span>
        </label>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {[['draw', 'Draw'], ['type', 'Type']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ flex: 1, background: tab === k ? '#1f1709' : C.card2, border: '1px solid ' + (tab === k ? C.gold : C.line), color: tab === k ? C.gold : C.text, borderRadius: 10, fontWeight: 700, fontSize: 14, padding: 12, cursor: 'pointer', fontFamily: 'inherit' }}>{l}</button>
          ))}
        </div>

        {tab === 'draw' ? (
          <div>
            <div style={{ background: '#fff', borderRadius: 10, position: 'relative', touchAction: 'none' }}>
              <canvas ref={canvasRef}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                style={{ display: 'block', width: '100%', height: 180, borderRadius: 10, cursor: 'crosshair' }} />
              {!drawn && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 10, textAlign: 'center', color: '#c9c9c9', fontSize: 13, pointerEvents: 'none' }}>Sign with your finger or mouse</div>}
            </div>
            <button onClick={clearPad} style={{ background: 'none', border: 'none', color: C.muted, fontWeight: 700, fontSize: 13, textDecoration: 'underline', cursor: 'pointer', marginTop: 8, fontFamily: 'inherit' }}>Clear</button>
          </div>
        ) : (
          <div>
            <div style={{ background: '#fff', borderRadius: 10, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '0 16px' }}>
              <span style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700, color: '#10243f', fontSize: 'clamp(30px,11vw,52px)', whiteSpace: 'nowrap' }}>{typed.trim() || data.candidate_name}</span>
            </div>
            <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type your full legal name" style={{ width: '100%', marginTop: 10, background: C.card2, border: '1px solid ' + C.line, borderRadius: 9, color: C.text, fontSize: 16, padding: '11px 12px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
        )}

        {err && <p style={{ color: '#e63c3c', fontSize: 14, marginTop: 12 }}>{err}</p>}
        <button onClick={submit} disabled={!canSign} style={{ width: '100%', marginTop: 14, background: canSign ? C.gold : C.card2, color: canSign ? '#1a1208' : C.muted, border: 'none', borderRadius: 11, fontWeight: 800, fontSize: 16, padding: 14, cursor: canSign ? 'pointer' : 'default', fontFamily: 'inherit' }}>
          {busy ? 'Signing…' : 'Sign & accept offer'}
        </button>
        <p style={{ color: C.muted, fontSize: 12, marginTop: 8, textAlign: 'center' }}>By signing you accept the offer above. A copy is saved to your candidate record.</p>
      </div>
      <div style={{ height: 30 }} />
    </div>
  );
}
